import { AfterViewInit, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PasswordManagementService } from 'src/app/shared/services/password-management.service';

export interface AdminChangePasswordDialogData {
	id: string;
	name: string;
	email: string;
}

const passwordsMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
	const password = control.get('password')?.value;
	const confirmPassword = control.get('confirmPassword')?.value;

	if (!confirmPassword) {
		return null;
	}

	return password === confirmPassword ? null : { passwordMismatch: true };
};

@Component({
	selector: 'app-admin-change-password-dialog',
	templateUrl: './admin-change-password-dialog.component.html',
	styleUrls: ['./admin-change-password-dialog.component.scss']
})
export class AdminChangePasswordDialogComponent implements AfterViewInit {
	@ViewChild('passwordInput') passwordInput?: ElementRef<HTMLInputElement>;
	passwordForm: FormGroup;
	isSubmitting = false;
	errorMessage = '';
	passwordType: 'password' | 'text' = 'password';
	confirmPasswordType: 'password' | 'text' = 'password';

	constructor(
		private formBuilder: FormBuilder,
		private passwordManagementService: PasswordManagementService,
		private dialogRef: MatDialogRef<AdminChangePasswordDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: AdminChangePasswordDialogData
	) {
		this.passwordForm = this.formBuilder.group(
			{
				password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(128)]],
				confirmPassword: ['', Validators.required],
				forcePasswordChange: [false]
			},
			{ validators: passwordsMatchValidator }
		);
	}

	ngAfterViewInit(): void {
		setTimeout(() => this.passwordInput?.nativeElement.focus());
	}

	async submit(): Promise<void> {
		if (this.isSubmitting) {
			return;
		}

		if (this.passwordForm.invalid) {
			this.passwordForm.markAllAsTouched();
			return;
		}

		this.isSubmitting = true;
		this.errorMessage = '';
		const formValue = this.passwordForm.getRawValue();
		this.passwordForm.disable({ emitEvent: false });

		try {
			const password = String(formValue.password || '');
			const forcePasswordChange = formValue.forcePasswordChange === true;
			const response = await this.passwordManagementService.adminSetPassword(
				this.data.id,
				password,
				forcePasswordChange
			);

			if (!response?.success) {
				throw new Error('INVALID_PASSWORD_UPDATE_RESPONSE');
			}

			this.passwordForm.reset();
			this.dialogRef.close({
				success: true,
				mustChangePassword: response.mustChangePassword
			});
		} catch (error: any) {
			this.errorMessage = this.getErrorMessage(error);
		} finally {
			this.passwordForm.enable({ emitEvent: false });
			this.isSubmitting = false;
		}
	}

	cancel(): void {
		if (!this.isSubmitting) {
			this.passwordForm.reset();
			this.dialogRef.close();
		}
	}

	toggleVisibility(field: 'password' | 'confirmPassword'): void {
		if (field === 'password') {
			this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
			return;
		}

		this.confirmPasswordType = this.confirmPasswordType === 'password' ? 'text' : 'password';
	}

	controlHasError(controlName: string, errorName: string): boolean {
		const control = this.passwordForm.get(controlName);
		return !!(control?.touched && control.hasError(errorName));
	}

	get userInitial(): string {
		const identity = String(this.data.name || this.data.email || '?').trim();
		return identity.charAt(0).toUpperCase() || '?';
	}

	get forcePasswordChange(): boolean {
		return this.passwordForm.get('forcePasswordChange')?.value === true;
	}

	private getErrorMessage(error: any): string {
		const code = String(error?.code || error?.error?.status || '');
		const backendMessage = error?.message || error?.error?.message;

		if (code.includes('permission-denied') || code.includes('PERMISSION_DENIED')) {
			return 'You do not have permission to change this user password.';
		}
		if (code.includes('not-found') || code.includes('NOT_FOUND')) {
			return 'The selected user account could not be found.';
		}
		if (code.includes('failed-precondition') || code.includes('FAILED_PRECONDITION')) {
			return backendMessage || 'This user password cannot be changed.';
		}
		if (code.includes('invalid-argument') || code.includes('INVALID_ARGUMENT')) {
			return backendMessage || 'The password does not meet the required policy.';
		}
		if (code.includes('unauthenticated') || code.includes('UNAUTHENTICATED')) {
			return 'Your session has expired. Please log in again.';
		}

		return 'Unable to change the password right now. Please try again.';
	}
}
